<template>
  <ion-page :lang="$i18n.locale">
    <ion-header>
      <!-- <ion-toolbar>topbar</ion-toolbar> -->
    </ion-header>
    <NotificationPopup />
    <Toast class="whitespace-pre-line" />
    <ion-content
      :id="id"
      fullscreen="false"
      class="master"
      :scroll-events="enableScrollEvent"
      @ionScroll="onScroll"
    >
      <ExperienceModal
        ref="exp-modal"
        v-if="selectedExp"
        :show="showExpModal"
        @close-modal="toggleExpModal"
        :exp="selectedExp"
        :show-button="showExpButton"
      ></ExperienceModal>
      <div
        v-if="showFilterModal"
        class="z-[99] w-[100vw] h-[calc(--app-height)] fixed inset-0 bg-[#0B0C0F99] bg-opacity-50 flex items-center md:justify-center"
      >
        <div
          v-if="showFilterModal"
          class="z-[99] w-[100vw] h-[calc(--app-height)] lg:w-[400px] lg:h-[600px] fixed lg:static inset-0"
        >
          <FilterModal
            :filter-items="filterModalItems"
            :filter-header="filterModalHeader"
            :filter-type="filterModalType"
            :filter-type-id="filterModalTypeId"
          />
        </div>
      </div>
      <LogoutPopup :show="showLogoutPopup" @close-modal="toggleLogoutPopup()"></LogoutPopup>
      <PhotoDownloadModal
        :show="showPhotoPopup"
        @close-modal="togglePhotoPopup()"
      ></PhotoDownloadModal>
      <NewsletterModal
        :show="showNewsletterPopup"
        :title="$t('copy.masterLayout.newsLetterModal.header')"
        :description="$t('copy.masterLayout.newsLetterModal.description')"
        :show-name="true"
        :subscribe="$t('copy.masterLayout.newsLetterModal.subscribe')"
        :is-thai="$i18n.locale === 'th'"
        @close-modal="toggleNewsletterPopup()"
      ></NewsletterModal>

      <main class="lg:flex h-full lg:h-auto">
        <SideBar class="shrink-0" @open-modal="toggleLogoutPopup()" />
        <!-- pb clears the floating bottom nav pill (68px pill + 16px gap, <lg only).
             Pages with self-managed full-viewport scroll layouts opt out via no-bottom-pad.
             min-w-0 lets the flex item shrink beside the sidebar despite wide inner content. -->
        <div class="flex-1 min-w-0" :class="noBottomPad ? 'h-full lg:h-auto' : 'pb-28 lg:pb-0'">
          <slot
            test="test"
            :toggleExp="toggleExpModal"
            :togglePhoto="togglePhotoPopup"
            :toggleNewsletter="toggleNewsletterPopup"
            :closeExp="closeExpModal"
          >
          </slot>
        </div>
      </main>
    </ion-content>
    <ion-footer
      v-if="!hideFooter"
      :class="{
        hidden: isKeyboardOpen,
      }"
    >
      <ion-toolbar class="bg-transparent lg:hidden">
        <BottomNav />
      </ion-toolbar>
    </ion-footer>
  </ion-page>
</template>
<script>
import { IonPage, IonHeader, IonContent, IonFooter, IonToolbar, isPlatform } from '@ionic/vue';
import { StatusBar } from '@capacitor/status-bar';
import BottomNav from '@/components/common/BottomNav';
import SideBar from '@/components/common/SideBar';
import LogoutPopup from '../components/HomePage/LogoutPopup.vue';
import NotificationPopup from '@/components/common/NotificationPopup.vue';
import Toast from '@/components/common/Toast.vue';
import ExperienceModal from '../components/ExperiencePage/ExperienceModal.vue';
import PhotoDownloadModal from '../components/Photo/PhotoDownloadModal.vue';
import NewsletterModal from '@/components/ResourcesPage/NewsletterModal.vue';
import store from '@/store';
import platformMixin from '@/mixins/platformMixin';
import { mapState } from 'vuex';
import FilterModal from '@/components/ResourcesPage/FilterModal.vue';
import { isGICPage } from '@/utils/conference-type';

export default {
  mixins: [platformMixin],
  components: {
    IonPage,
    IonHeader,
    IonContent,
    IonFooter,
    IonToolbar,
    BottomNav,
    SideBar,
    LogoutPopup,
    NotificationPopup,
    Toast,
    ExperienceModal,
    PhotoDownloadModal,
    FilterModal,
    NewsletterModal,
  },
  props: {
    pageTitle: {
      type: String,
      default: '',
    },
    enableScrollEvent: {
      type: Boolean,
      default: false,
    },
    noLogin: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: 'ion-content',
    },
    noBottomPad: {
      type: Boolean,
      default: false,
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showLogoutPopup: false,
      showPhotoPopup: false,
      showNewsletterPopup: false,
      showExpModal: false,
      selectedExp: null,
      showExpButton: false,
      isKeyboardOpen: false,
    };
  },
  computed: {
    ...mapState('profile', ['conferences']),
    ...mapState('filter', [
      'showFilterModal',
      'selectedFilter',
      'filterTypeScheduleMapping',
      'schedule',
      'topics',
      'filterType',
    ]),
    isGICPage() {
      return isGICPage();
    },
    gicFilterItems() {
      if (!this.isGICPage || !this.selectedFilter || this.selectedFilter.typeId === null) {
        return [];
      }
      return this.filterTypeScheduleMapping[this.selectedFilter.typeId] || [];
    },
    gicFilterHeader() {
      if (!this.isGICPage || !this.selectedFilter) {
        return '';
      }
      return this.$t(
        `copy.resourcesPage.filterSection.gic-${this.selectedFilter.typeId}`,
        `GIC ${this.selectedFilter.typeId}`,
      );
    },
    gicFilterType() {
      return this.selectedFilter?.type || 'Sessions';
    },
    gicFilterTypeId() {
      return this.selectedFilter?.typeId || null;
    },
    // Computed properties for GPC/GPCR pages
    gpcFilterItems() {
      // Return schedule or topics based on filterType from store
      if (this.filterType === 'Topics') {
        return Array.isArray(this.topics) ? this.topics : [];
      }
      // Default: Sessions
      return Array.isArray(this.schedule) ? this.schedule : [];
    },
    gpcFilterHeader() {
      // Return translated header based on filterType
      if (this.filterType === 'Topics') {
        return this.$t('copy.resourcesPage.filterSection.topics');
      }
      return this.$t('copy.resourcesPage.filterSection.sessions');
    },
    gpcFilterType() {
      return this.filterType || 'Sessions';
    },
    gpcFilterTypeId() {
      // GPC/GPCR doesn't use typeId
      return null;
    },
    // Unified computed properties that work for both GIC and GPC/GPCR
    filterModalItems() {
      if (this.isGICPage) {
        return this.gicFilterItems;
      }
      return this.gpcFilterItems;
    },
    filterModalHeader() {
      if (this.isGICPage) {
        return this.gicFilterHeader;
      }
      return this.gpcFilterHeader;
    },
    filterModalType() {
      if (this.isGICPage) {
        return this.gicFilterType;
      }
      return this.gpcFilterType;
    },
    filterModalTypeId() {
      if (this.isGICPage) {
        return this.gicFilterTypeId;
      }
      return this.gpcFilterTypeId;
    },
    dropdownItems() {
      if (this.clickedFilter && this.clickedFilter.type === 'Sessions') {
        return this.filterTypeScheduleMapping[this.clickedFilter.typeId] || [];
      }
      // Fallback to all schedule for non-GIC or when clickedFilter is not set
      return this.schedule || [];
    },
  },
  async mounted() {
    if (!this.noLogin) {
      // await StatusBar.setOverlaysWebView({ overlay: true });
      await store.dispatch('profile/fetchProfile');
      // Fetch account-age + memento flags at startup so the bottom nav can decide
      // where the PhotoDrop tab routes (welcome page vs straight out to memento).
      store.dispatch('profile/fetchIsNewAccount');
      store.dispatch('cms/fetchMementoEnable');
    }
    // Hide the floating bottom nav while the keyboard is up (iOS + Android).
    // keyboardWill* (Capacitor) fires before the animation for a snappy hide;
    // ionKeyboardDid* kept as a fallback where only Ionic's events fire.
    window.addEventListener('keyboardWillShow', this.onKeyboardShow);
    window.addEventListener('keyboardWillHide', this.onKeyboardHide);
    window.addEventListener('ionKeyboardDidShow', this.onKeyboardShow);
    window.addEventListener('ionKeyboardDidHide', this.onKeyboardHide);
  },
  beforeUnmount() {
    window.removeEventListener('keyboardWillShow', this.onKeyboardShow);
    window.removeEventListener('keyboardWillHide', this.onKeyboardHide);
    window.removeEventListener('ionKeyboardDidShow', this.onKeyboardShow);
    window.removeEventListener('ionKeyboardDidHide', this.onKeyboardHide);
  },
  async ionViewWillEnter() {
    if (isPlatform('android') && !isPlatform('mobileweb')) {
      // Display content under transparent status bar (Android only)
      await StatusBar.setOverlaysWebView({ overlay: true });
    }
  },
  ionViewWillLeave() {
    this.$el.querySelector('ion-content')?.scrollToTop(0);
  },
  methods: {
    onKeyboardShow() {
      this.isKeyboardOpen = true;
    },
    onKeyboardHide() {
      this.isKeyboardOpen = false;
    },
    toggleLogoutPopup() {
      this.showLogoutPopup = !this.showLogoutPopup;
    },
    togglePhotoPopup() {
      this.showPhotoPopup = !this.showPhotoPopup;
    },
    toggleNewsletterPopup() {
      this.showNewsletterPopup = !this.showNewsletterPopup;
    },
    closeExpModal() {
      this.showExpModal = false;
    },
    toggleExpModal(exp, showButton) {
      this.showExpButton = showButton;
      this.showExpModal = !this.showExpModal;
      this.selectedExp = exp;
      this.$nextTick(() => {
        if (this.$refs['exp-modal']) {
          this.$refs['exp-modal'].update();
        }
      });
    },
    onScroll(event) {
      this.$emit('on-scroll', event);
    },
  },
};
</script>
<style scoped>
ion-toolbar {
  --border-color: transparent;
  --background: transparent;
  --ion-toolbar-background: transparent;
  --min-height: 0;
  --padding-bottom: 0;
  --padding-end: 0;
  --padding-start: 0;
  --padding-top: 0;
  --ion-color-base: transparent !important;
  background: transparent !important;
  height: auto;
  min-height: 0;
  /* for drop shadow */
  contain: none;
  overflow: visible;
}

ion-footer {
  --background: transparent;
  --ion-toolbar-background: transparent;
  background: transparent !important;
  bottom: 0;
  box-shadow: none;
  height: auto;
  left: 0;
  /* Floating pill: one small, controlled gap on all platforms. The safe-area
     inset is intentionally NOT added here — see the toolbar override below. */
  padding-bottom: 16px;
  pointer-events: none;
  position: fixed;
  right: 0;
  z-index: 40;
}

ion-toolbar::part(background),
ion-footer::part(background) {
  background: transparent !important;
}

ion-footer ion-toolbar {
  height: auto;
  min-height: 0;
  pointer-events: none;
}

/* Ionic auto-pads the footer's last toolbar by --ion-safe-area-bottom
   (~34px on notched iOS) to clear the home indicator. That's for a solid
   docked bar; our nav is a transparent floating pill, so it just shows as an
   empty gap. Neutralize it and let the footer control the gap. */
ion-footer ion-toolbar:last-of-type {
  padding-bottom: 0;
}

ion-footer ion-toolbar::part(container) {
  min-height: 0;
  padding: 0;
  /* for drop shadow */
  overflow: visible;
  contain: none;
}

ion-footer nav {
  pointer-events: auto;
}

ion-content.master {
  --padding-bottom: 0px;
  --background: #ffffff;
}

.footer-md::before {
  display: none;
}

/* ion-footer {
  position: fixed;
  bottom: 0;
  width: 100vw;
} */

.box-shadow {
  box-shadow: 0px 1px 25px 0px #0000001a;
}
</style>

<style>
@media (max-width: 1023.98px) {
  ion-app,
  ion-router-outlet > .ion-page,
  .ion-page,
  body {
    --ion-background-color: #ffffff;
    background-color: #ffffff;
  }
}
</style>
